import React, { PropsWithChildren, useContext, ReactElement } from 'react';

import PropTypes from 'prop-types';
import { Cell } from 'react-table';

import { stylePropType, StylableComponent } from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';

import TablePropTypes from './propTypes';
import TableSizeThemeContext from './TableSizeThemeContext';

export type TableBodyCellProps<D extends Record<string, unknown>> =
  StylableComponent<HTMLDivElement> & {
    cell: Cell<D>;
  };

const TableBodyCell = <D extends Record<string, unknown>>({
  cell,
  children,
  style,
  ...props
}: PropsWithChildren<TableBodyCellProps<D>>): ReactElement => {
  const { tableSizeTheme } = useContext(TableSizeThemeContext);
  return (
    <div
      role="cell"
      css={[
        {
          maxWidth: cell.column.maxWidth,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          borderBottom: `1px solid ${theme.colors.charcoal100}`,
          padding: `0 ${tableSizeTheme.cellPadding}px`,
          borderRight: `1px solid ${
            cell.column.isResizing ? theme.colors.adskBlue300 : 'transparent'
          }`,
          backgroundColor: theme.colors.white,
        },
        style,
      ]}
      {...props}
    >
      {React.isValidElement(children)
        ? React.cloneElement(children, { cell })
        : cell.render('Cell')}
    </div>
  );
};

TableBodyCell.propTypes = {
  children: PropTypes.node,
  cell: TablePropTypes.cell.isRequired,
  style: stylePropType,
};

export default TableBodyCell;
